import { NgTemplateOutlet } from '@angular/common';
import { Component, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule } from '@ngx-translate/core';
import { TEMPLATE_CONSTANT } from '../../../constants/template.constant';
import { TRANSLATION_TEMPLATES } from '../../../constants/translation-templates-constants';
import { CustomBookingMessageTemplate } from '../../../db-models/widget-conf-dao';
import { EventCartItem, GuestModel } from '../../../models/cart.model';
import { Partner } from '../../../models/global';
import { EventState } from '../../../models/state.model';
import { WidgetColorConf } from '../../../models/widget-color.model';
import { CustomEventService } from '../../../services/custom-event.service';
import { HelperService } from '../../../services/helper.service';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { MatIconComponent } from '../../../shared/components/mat-icon/mat-icon.component';
import { CalioAddRequiredPipe } from '../../../shared/pipes/calio-add-required.pipe';
import { CalioMeetingTemplatePipe } from '../../../shared/pipes/calio-meeting-template.pipe';
import { TranslationPipe } from '../../../shared/pipes/translation.pipe';
import { CwIntlTelInputComponent } from '../theme/cw-intl-tel-input/cw-intl-tel-input.component';

const Components = [MatIconComponent, ButtonComponent, CwIntlTelInputComponent]
const pipes = [CalioAddRequiredPipe, CalioMeetingTemplatePipe, TranslationPipe]

export type GuestEntryMode = 'named_only' | 'anonymous_only' | 'both';

@Component({
  selector: 'app-additional-guest',
  templateUrl: './additional-guest.component.html',
  styleUrls: ['./additional-guest.component.scss'],
  imports: [FormsModule, FontAwesomeModule, TranslateModule, NgTemplateOutlet, ...Components, ...pipes]
})
export class AdditionalGuestComponent {

  @Input() eventState: EventState;
  @Input() cart?: EventCartItem[] = [];
  @Input() additionalGuests: GuestModel[];
  @Input() numberOfAllowedGuest = 0;
  @Input() partner: Partner;
  @Input() widgetTemplates: CustomBookingMessageTemplate[] = [];
  @Input() lang: string;
  @Input() anonymousGuestCount = 0;
  @Input() widgetColorConf: WidgetColorConf;
  /**
   * Per-event entry mode coming from event.guest_entry_mode.
   * Decides which sections render (no toggle in v2):
   *   - 'named_only'     → only the per-guest form section
   *   - 'anonymous_only' → only the stepper section
   *   - 'both'           → both sections stacked, customer fills either or both
   */
  @Input() guestEntryMode: GuestEntryMode = 'named_only';

  @Output() updateAdditionalGuestsEvent = new EventEmitter<GuestModel[]>();
  @Output() anonymousGuestCountChange = new EventEmitter<number>();

  private helperSevice = inject(HelperService);
  private customEventService = inject(CustomEventService);

  protected readonly svgIcons = [
    { name: 'close', path: 'assets/images/material/close.svg' },
    { name: 'edit', path: 'assets/images/material/edit.svg' },
    { name: 'person_add', path: 'assets/images/material/person_add.svg' },
    { name: 'person_filled', path: 'assets/images/material/person_fillded.svg' },
    { name: 'group', path: 'assets/images/material/group.svg' },
    { name: 'add', path: 'assets/images/material/add.svg' }
  ]
  protected readonly templateContent = TRANSLATION_TEMPLATES;
  protected readonly templateConstant = TEMPLATE_CONSTANT;
  protected disableAdditionalParticipantsEditing = false;

  /**
   * Reactive computed visibility for the "Add participant" tile in the named
   * section. Hides when the COMBINED total (named + anonymous) hits the cap,
   * so the booker can't keep adding named guests after the anonymous stepper
   * already filled the remaining seats. Mirrors the cap math used by
   * getMaxAnonymousAllowed() so both sides stay in sync.
   */
  protected get showAddAdditionalGuestButton(): boolean {
    if (this.disableAdditionalParticipantsEditing) return false;
    const named = this.additionalGuests?.length ?? 0;
    const anon = this.anonymousGuestCount ?? 0;
    const cap = this.numberOfAllowedGuest ?? 0;
    return (named + anon) < cap;
  }
  protected isGuestEditModeEnable = false;
  protected tempAdditionalGuests = new GuestModel();
  protected tempAdditionalGuestIndex: number;
  /** Show named-guest section when event allows named (named_only or both). */
  protected get showNamedSection(): boolean {
    return this.guestEntryMode === 'named_only' || this.guestEntryMode === 'both';
  }
  /** Show anonymous-count section when event allows anonymous (anonymous_only or both). */
  protected get showAnonymousSection(): boolean {
    return this.guestEntryMode === 'anonymous_only' || this.guestEntryMode === 'both';
  }
  // Anonymous mode mirrors the individual UX: when the user is entering
  // a count we render the input + Save/Cancel buttons; once saved we render
  // a locked summary card with edit/remove icons. tempAnonymousCount is the
  // edit-buffer so Cancel can restore the previous value.
  protected anonymousEditMode = false;
  protected tempAnonymousCount = 0;

  @ViewChild('additionalGuestForm') additionalGuestForm: NgForm;

  constructor() {
    this.customEventService.paypalOrderSuccessfullEvent.subscribe(() => {
      // showAddAdditionalGuestButton is a getter that returns false once
      // disableAdditionalParticipantsEditing is true, so no direct write needed.
      this.disableAdditionalParticipantsEditing = true;
    });
    this.helperSevice.registerSvgIconsList(this.svgIcons);
  }

  /**
   * Max anonymous guests the booker can request. Uses the same cap as the
   * "You can add up to N guests" label (numberOfAllowedGuest input, computed
   * in personal-data-form as min(slot.available_regular_capacity - 1,
   * event.number_allowed_additional_guests)).
   * Subtracts any named guests already added so the combined total never
   * exceeds the slot's actual capacity.
   * Floored at 0 to avoid negative bounds when at cap.
   */
  protected getMaxAnonymousAllowed(): number {
    const cap = this.numberOfAllowedGuest ?? 0;
    const named = this.additionalGuests?.length ?? 0;
    return Math.max(cap - named, 0);
  }

  protected enterAnonymousEdit(): void {
    if (this.disableAdditionalParticipantsEditing) {
      return;
    }
    this.tempAnonymousCount = this.anonymousGuestCount;
    this.anonymousEditMode = true;
  }

  protected cancelAnonymousEdit(): void {
    // Discard buffered changes; keep the committed count.
    this.tempAnonymousCount = this.anonymousGuestCount;
    this.anonymousEditMode = false;
  }

  protected saveAnonymousCount(): void {
    if (this.disableAdditionalParticipantsEditing) {
      return;
    }
    // Cap = real remaining capacity. getMaxAnonymousAllowed() already
    // subtracts named guests; we don't add back the currently-saved
    // anonymousGuestCount because the user is REPLACING it, not adding.
    const max = this.getMaxAnonymousAllowed();
    let value = Number(this.tempAnonymousCount);
    if (!Number.isFinite(value) || value < 0) {
      value = 0;
    }
    if (value > max) {
      value = max;
    }
    value = Math.trunc(value);
    this.anonymousGuestCount = value;
    this.tempAnonymousCount = value;
    this.anonymousGuestCountChange.emit(value);
    this.anonymousEditMode = false;
    // Saving "0" is equivalent to removing — caller sees the empty-state
    // tile again instead of a "0 guests" card that does nothing useful.
  }

  /** Always-safe number coercion — ngModel + type="number" can leave the
   *  bound value as a string ("3") after typing, which made `<=`/`>=`
   *  comparisons and Math.trunc behave inconsistently and caused the
   *  decrement button to sometimes appear unresponsive. */
  protected get tempCountSafe(): number {
    const v = Number(this.tempAnonymousCount);
    return Number.isFinite(v) && v >= 0 ? Math.trunc(v) : 0;
  }

  /** Stepper +/- handler — guarded against going below 0 or above cap. */
  protected stepAnonymousCount(delta: number): void {
    if (this.disableAdditionalParticipantsEditing) {
      return;
    }
    const max = this.getMaxAnonymousAllowed();
    const next = Math.min(Math.max(this.tempCountSafe + delta, 0), max);
    this.tempAnonymousCount = next;
  }

  /** Sanitize direct keyboard input so out-of-range values can't sneak
   *  through. Keeps the warning visible briefly if user types over the cap
   *  before we clamp back, so they see the message + understand why. */
  protected onTempAnonymousChange(raw: unknown): void {
    let value = Number(raw);
    if (!Number.isFinite(value) || value < 0) {
      value = 0;
    }
    // Strip leading zeros and decimals — "02" → 2, "3.7" → 3.
    // Don't clamp to max here so the inline warning can show; Save handler
    // clamps as the final safeguard.
    this.tempAnonymousCount = Math.trunc(value);
  }

  /** On blur, force the DOM input to redisplay the normalized number so
   *  transient strings like "02" or "  3 " become "2" / "3". */
  protected onTempAnonymousBlur(event: FocusEvent): void {
    const clean = this.tempCountSafe;
    this.tempAnonymousCount = clean;
    const target = event.target as HTMLInputElement | null;
    if (target && target.value !== String(clean)) {
      target.value = String(clean);
    }
  }

  protected removeAnonymousCount(): void {
    if (this.disableAdditionalParticipantsEditing) {
      return;
    }
    this.anonymousGuestCount = 0;
    this.tempAnonymousCount = 0;
    this.anonymousGuestCountChange.emit(0);
    this.anonymousEditMode = false;
  }

  private addAdditionalGuest(): void {
    // showAddAdditionalGuestButton is a reactive getter (combined named + anon
    // cap) — no explicit assignments needed here; tile re-evaluates on each
    // change-detection tick. We still gate the push() on capacity so the tile
    // never adds beyond the limit even if it was somehow clickable.
    if (this.cart.length === 1) {
      if (
        this.additionalGuests?.length < (this.cart[0].available_regular_capacity - 1) &&
        this.additionalGuests?.length < this.eventState.number_allowed_additional_guests
      ) {
        this.additionalGuests.push(this.tempAdditionalGuests);
      } else if (this.cart[0].is_waitinglist_enabled) {
        let waitingListCapcaity = 0;
        if (this.cart[0].regular_booking_count === this.cart[0].regular_capacity) {
          waitingListCapcaity = (this.cart[0].available_waitinglist_capacity - this.additionalGuests?.length - 1);
        }
        if (waitingListCapcaity) {
          this.additionalGuests.push(this.tempAdditionalGuests);
        }
      }
    } else {
      const regularCapacityArray = this.cart.map(cartItem => cartItem.available_regular_capacity).sort();
      const minRegularCapacity = regularCapacityArray[0] || 0;

      if (this.additionalGuests?.length < (minRegularCapacity - 1)) {
        this.additionalGuests.push(this.tempAdditionalGuests);
      } else {
        const minimumWaitingListCapacityArray = this.cart.map(cartItem =>
          cartItem.is_waitinglist_enabled
            ? (cartItem.available_waitinglist_capacity - this.additionalGuests?.length - 1)
            : 0
        ).sort();
        const waitingListCapcaity = minimumWaitingListCapacityArray[0] || 0;
        if (waitingListCapcaity) {
          this.additionalGuests.push(this.tempAdditionalGuests);
        }
      }
    }
  }

  protected addNewAdditionalGuest(): void {
    this.isGuestEditModeEnable = true;
  }

  protected editAdditionalGuests(guest: GuestModel, index: number): void {
    this.tempAdditionalGuests = guest;
    this.tempAdditionalGuestIndex = index;
    this.isGuestEditModeEnable = true;
  }

  protected cancel(): void {
    this.resetAdditionalGuestForm();
  }

  protected save(): void {
    // Once the payment lock engages, an already-open form must not mutate the guest list:
    // updateAdditionalGuests() suppresses the emit, so local and parent copies would diverge.
    if (this.disableAdditionalParticipantsEditing) {
      this.resetAdditionalGuestForm();
      return;
    }
    this.additionalGuestForm.control.markAllAsTouched();
    if (this.additionalGuestForm.invalid) return;

    if (this.tempAdditionalGuestIndex === 0 || this.tempAdditionalGuestIndex) {
      this.additionalGuests[this.tempAdditionalGuestIndex] = this.tempAdditionalGuests;
    } else {
      this.addAdditionalGuest();
    }
    this.resetAdditionalGuestForm();
  }

  private resetAdditionalGuestForm(): void {
    this.isGuestEditModeEnable = false;
    this.tempAdditionalGuests = new GuestModel();
    this.tempAdditionalGuestIndex = undefined;
    this.updateAdditionalGuests();
  }

  protected removeAdditionalGuest(index: number): void {
    // Without this guard the splice still runs while updateAdditionalGuests()
    // suppresses the emit, so the local list and the parent's copy diverge.
    if (this.disableAdditionalParticipantsEditing) {
      return;
    }
    this.additionalGuests.splice(index, 1);
    // Tile visibility is a reactive getter; no explicit reset needed.
    this.updateAdditionalGuests();
  }

  private updateAdditionalGuests(): void {
    if (this.disableAdditionalParticipantsEditing) {
      return;
    }

    const totalGuests = this.additionalGuests.filter(guests => (guests?.prename.trim() !== '' && guests?.lastname.trim() !== ''));
    this.updateAdditionalGuestsEvent.emit(totalGuests);
  }
}
